import { Box, InfoDot, ProgressBar, Text } from '@factorialco/deprecated-design-system'
import { BudgetFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { amountWithCurrency, budgetProgress } from 'modules/finance/lib/budgets'

import * as styles from '../index.vanilla.css'

interface Props {
  budget: BudgetFragment
}

const BudgetProgress = ({ budget }: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const progress = budgetProgress(budget.periodStatus)

  const overBudget = budget.periodStatus === 'over_budget'
  const availableAmount = overBudget
    ? budget.spentAmountCents - budget.amountCents
    : budget.availableAmountCents

  return (
    <>
      <Box className={styles.periodInfo}>
        <Box gap="s12">
          <Text.Normal color="grey1000">{i18n.t('budgets.detail.period_status.title')}</Text.Normal>
          <Text.Header1>
            {amountWithCurrency(
              budget.amountCents ? Number(budget.amountCents) : 0,
              budget.currency,
              locale
            )}
          </Text.Header1>
        </Box>

        <Box flexDirection="row" gap="s24">
          <Box aria-label="Period start">
            <Text.Normal weight="medium">{i18n.t('common.from')}</Text.Normal>
            <Text.Normal>{dateBackendDateTimeToUI(budget.periodStart)}</Text.Normal>
          </Box>
          <Box aria-label="Period end">
            <Text.Normal weight="medium">{i18n.t('common.to')}</Text.Normal>
            <Text.Normal>
              {budget.periodEnd
                ? dateBackendDateTimeToUI(budget.periodEnd)
                : i18n.t('budgets.detail.period_status.indefinite')}
            </Text.Normal>
          </Box>
        </Box>
      </Box>

      <Box width="full" gap="s32" paddingX="s24" paddingBottom="s32">
        <Box flexDirection="row">
          <Box gap="s12" width="full">
            <Text.Normal color="grey1000">
              {i18n.t('budgets.detail.period_status.amount.reported')}
            </Text.Normal>

            <Box gap="s6" flexDirection="row" alignItems="center">
              <InfoDot size="medium" color={progress.progressColor} />
              <Text.Header1>
                {amountWithCurrency(
                  budget.spentAmountCents ? Number(budget.spentAmountCents) : 0,
                  budget.currency,
                  locale
                )}
              </Text.Header1>
            </Box>
          </Box>

          <Box gap="s12" width="full">
            <Text.Normal color="grey1000">
              {overBudget
                ? i18n.t('budgets.detail.period_status.amount.overspent')
                : i18n.t('budgets.detail.period_status.amount.available')}
            </Text.Normal>

            <Box gap="s6" flexDirection="row" alignItems="center">
              <InfoDot size="medium" color={progress.barColor} />
              <Text.Header1 color={overBudget ? 'radicalRed1200' : 'grey1000'}>
                {amountWithCurrency(Math.abs(availableAmount), budget.currency, locale)}
              </Text.Header1>
            </Box>
          </Box>
        </Box>

        <ProgressBar
          showLabel={false}
          barColor={progress.barColor}
          progressColor={progress.progressColor}
          amount={budget.spentAmountCents}
          total={budget.amountCents}
        />
      </Box>
    </>
  )
}

export default BudgetProgress
